/**
 * Branding & Legal store (frontend, localStorage demo).
 *
 * Permet à l'admin d'écraser certaines valeurs du catalogue (logo, mentions
 * légales) sans toucher au code. En Phase 1, ces données passeront sur
 * `/api/admin/branding` et `/api/admin/legal`.
 */

const BRANDING_KEY = "le11_admin_branding_v1";
const LEGAL_KEY = "le11_admin_legal_v1";
const RESTAURANT_KEY = "le11_admin_restaurant_v1";

// ── Defaults ───────────────────────────────────────────────────────────────

export const BRANDING_DEFAULTS = {
  logo_url:
    "https://customer-assets.emergentagent.com/job_ordering-le11/artifacts/zpxbuuoo_logo%20le%2011.png",
  logo_transparent_url:
    "https://customer-assets.emergentagent.com/job_ordering-le11/artifacts/zpxbuuoo_logo%20le%2011.png",
  og_image_url:
    "https://customer-assets.emergentagent.com/job_ordering-le11/artifacts/akgved2s_facade%20restaurant.png",
};

export const LEGAL_DEFAULTS = {
  establishment_name: "LE 11",
  legal_name: "",
  legal_form: "",
  siret: "",
  rcs: "",
  address_street: "11 Avenue Alexis Varagne",
  address_zip_city: "95400 Villiers-le-Bel",
  phone: "01 39 94 19 19",
  email: "contact@pizza11.fr",
  domain: "pizza11.fr",
  publication_director: "",
  host_provider: "",
  contact_email: "contact@pizza11.fr",
  additional_notice: "",
};

const MAPS_URL_DEFAULT =
  "https://www.google.com/maps/search/?api=1&query=11%20Avenue%20Alexis%20Varagne%2095400%20Villiers-le-Bel";

export const RESTAURANT_DEFAULTS = {
  name: "LE 11",
  slogan: "Votre pizza, à votre façon",
  address_street: "11 Avenue Alexis Varagne",
  address_zip: "95400",
  address_city: "Villiers-le-Bel",
  phone_display: "01 39 94 19 19",
  phone_tel: "tel:+33139941919",
  email: "contact@pizza11.fr",
  domain: "pizza11.fr",
  photo_url:
    "https://customer-assets.emergentagent.com/job_ordering-le11/artifacts/akgved2s_facade%20restaurant.png",
  maps_url: MAPS_URL_DEFAULT,
  // Horaires validés (Phase 0J)
  hours: [
    { day: "Lundi", range: "11h00 – 23h00" },
    { day: "Mardi", range: "11h00 – 23h00" },
    { day: "Mercredi", range: "11h00 – 23h00" },
    { day: "Jeudi", range: "11h00 – 23h00" },
    { day: "Vendredi", range: "11h00 – 03h00" },
    { day: "Samedi", range: "11h00 – 03h00" },
    { day: "Dimanche", range: "11h00 – 23h00" },
  ],
};

// ── Helpers ────────────────────────────────────────────────────────────────

const safeRead = (key, defaults) => {
  if (typeof window === "undefined") return { ...defaults };
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return { ...defaults };
    return { ...defaults, ...(JSON.parse(raw) || {}) };
  } catch {
    return { ...defaults };
  }
};

const safeWrite = (key, value) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
    // notify in-app listeners
    window.dispatchEvent(new CustomEvent("le11:settings-changed", { detail: { key } }));
  } catch {
    /* ignore */
  }
};

// ── Public API ─────────────────────────────────────────────────────────────

export const getBranding = () => safeRead(BRANDING_KEY, BRANDING_DEFAULTS);
export const setBranding = (next) => safeWrite(BRANDING_KEY, { ...BRANDING_DEFAULTS, ...next });
export const resetBranding = () => {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(BRANDING_KEY);
  window.dispatchEvent(new CustomEvent("le11:settings-changed", { detail: { key: BRANDING_KEY } }));
};

export const getLegal = () => safeRead(LEGAL_KEY, LEGAL_DEFAULTS);
export const setLegal = (next) => safeWrite(LEGAL_KEY, { ...LEGAL_DEFAULTS, ...next });
export const resetLegal = () => {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(LEGAL_KEY);
  window.dispatchEvent(new CustomEvent("le11:settings-changed", { detail: { key: LEGAL_KEY } }));
};

export const getRestaurant = () => safeRead(RESTAURANT_KEY, RESTAURANT_DEFAULTS);
export const setRestaurant = (next) =>
  safeWrite(RESTAURANT_KEY, { ...RESTAURANT_DEFAULTS, ...next });
export const resetRestaurant = () => {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(RESTAURANT_KEY);
  window.dispatchEvent(
    new CustomEvent("le11:settings-changed", { detail: { key: RESTAURANT_KEY } })
  );
};

// ── React hook ─────────────────────────────────────────────────────────────

import { useEffect, useState } from "react";

const useSettingsKey = (key, getter) => {
  const [val, setVal] = useState(getter);
  useEffect(() => {
    const handler = (e) => {
      if (!e?.detail || e.detail.key === key || e.detail.key === undefined) {
        setVal(getter());
      }
    };
    const onStorage = (e) => {
      if (e.key === key) setVal(getter());
    };
    window.addEventListener("le11:settings-changed", handler);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("le11:settings-changed", handler);
      window.removeEventListener("storage", onStorage);
    };
  }, [key, getter]);
  return val;
};

export const useBranding = () => useSettingsKey(BRANDING_KEY, getBranding);
export const useLegal = () => useSettingsKey(LEGAL_KEY, getLegal);
export const useRestaurant = () => useSettingsKey(RESTAURANT_KEY, getRestaurant);
